import { Link2, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatMoney, sumDecimals } from "@/lib/format";
import ConfirmDelete from "@/components/ConfirmDelete";

// Spreadsheet layout: one line per date + description (or split group), Exp./Credit columns per director.
export function groupLines(entries) {
  const by = new Map();
  for (const e of entries) {
    const k = e.split_group_id || `${e.date}|${e.description.trim().toLowerCase()}`;
    if (!by.has(k)) by.set(k, { key: k, date: e.date, description: e.description, split: !!e.split_group_id, entries: [] });
    by.get(k).entries.push(e);
  }
  return [...by.values()];
}

const Amount = ({ hits, kind, tone, last, onEdit }) => (
  <td className={`px-3 py-2 text-right font-mono ${tone} ${last ? "border-r border-border/60" : ""}`}>
    {hits.length > 0 && (
      <button type="button" onClick={() => onEdit(hits[0])} className="hover:underline" data-testid={`entry-edit-${hits[0].id}-${kind}`}>
        {formatMoney(sumDecimals(hits.map((e) => e[kind])))}
      </button>
    )}
  </td>
);

const Line = ({ line, directors, onEdit, onDelete }) => (
  <tr className="border-b border-border hover:bg-accent/30 transition-colors" data-testid={`entry-line-${line.entries[0].id}`}>
    <td className="px-4 py-2 font-mono text-xs whitespace-nowrap">{line.date}</td>
    <td className="px-4 py-2 min-w-[220px]">
      {line.split && <Link2 className="inline h-3.5 w-3.5 mr-1.5 text-indigo-300" aria-label="split entry" />}
      {line.description}
    </td>
    {directors.map((d) => {
      const mine = line.entries.filter((e) => e.director === d.id);
      return [
        <Amount key={`${d.id}-d`} hits={mine.filter((e) => e.debit !== "0")} kind="debit" tone="text-rose-300" onEdit={onEdit} />,
        <Amount key={`${d.id}-c`} hits={mine.filter((e) => e.credit !== "0")} kind="credit" tone="text-emerald-300" last onEdit={onEdit} />,
      ];
    })}
    <td className="px-2 py-2 text-right whitespace-nowrap">
      {line.entries.length === 1 && (
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(line.entries[0])} data-testid={`entry-edit-${line.entries[0].id}`}><Pencil className="h-4 w-4" /></Button>
      )}
      <ConfirmDelete name={`${line.description || "this line"}${line.entries.length > 1 ? ` (${line.entries.length} entries)` : ""}`}
        testId={`entry-delete-${line.entries[0].id}`} onConfirm={() => onDelete(line)} />
    </td>
  </tr>
);

export default function EntriesGrid({ lines, totalLines, directors, onEdit, onDelete }) {
  const all = (totalLines || lines).flatMap((l) => l.entries);
  return (
    <div className="border border-border bg-card overflow-x-auto">
      <table className="w-full text-sm" data-testid="entries-table">
        <thead>
          <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border bg-accent/40">
            <th className="px-4 py-3 font-medium text-left">Date</th>
            <th className="px-4 py-3 font-medium text-left">Description</th>
            {directors.map((d) => [
              <th key={`${d.id}-d`} className="px-3 py-3 font-medium text-right whitespace-nowrap">{d.name} Exp.</th>,
              <th key={`${d.id}-c`} className="px-3 py-3 font-medium text-right whitespace-nowrap border-r border-border/60">{d.name} Credit</th>,
            ])}
            <th />
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => <Line key={l.key} line={l} directors={directors} onEdit={onEdit} onDelete={onDelete} />)}
        </tbody>
        {lines.length > 0 && (
          <tfoot>
            <tr className="border-t-2 border-border font-semibold bg-accent/30">
              <td className="px-4 py-3" colSpan={2}>Total</td>
              {directors.map((d) => {
                const rows = all.filter((e) => e.director === d.id);
                return [
                  <td key={`${d.id}-td`} className="px-3 py-3 text-right font-mono text-rose-300" data-testid={`entries-total-debit-${d.name}`}>{formatMoney(sumDecimals(rows.map((r) => r.debit)))}</td>,
                  <td key={`${d.id}-tc`} className="px-3 py-3 text-right font-mono text-emerald-300 border-r border-border/60" data-testid={`entries-total-credit-${d.name}`}>{formatMoney(sumDecimals(rows.map((r) => r.credit)))}</td>,
                ];
              })}
              <td />
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
